import {
    FullscreenFrame,
    FullscreenHeader,
    FullscreenMain,
    FullscreenOverlay,
    FullscreenSidebar,
} from '@99designs/common/components/Fullscreen';
import { Icon } from '@99designs/common/components/Icon';
import { IconButton } from '@99designs/common/components/IconButton';
import Bugsnag from 'common/utils/Bugsnag';
import loadImage from 'common/utils/loadImage';
import Portal from 'projects/components/Portal';
import React, { useEffect, useRef, useState } from 'react';
import DesignerDetails, { Designer } from './DesignerDetails';
import LoadableImage from './LoadableImage';

type Props = {
    designer: Designer;
    onClose: () => void;
    selectedDesignIndex: number;
    onDesignChange: (index: number) => void;
};

const FileOverlay: React.FunctionComponent<Props> = props => {
    const [currentIndex, setCurrentIndex] = useState(props.selectedDesignIndex);

    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        async function loadImages() {
            const { designs } = props.designer.profile;
            await loadImage(designs[currentIndex].original);
            for (let i = 0; i < designs.length; i++) {
                if (i !== currentIndex) {
                    loadImage(designs[i].original).catch(err => Bugsnag.notify(err));
                }
            }
        }

        loadImages().catch(err => Bugsnag.notify(err));

        if (containerRef.current) {
            containerRef.current.focus();
        }
    }, [props.designer]);

    const hasPrev = () => currentIndex !== 0;

    const hasNext = () => currentIndex !== props.designer.profile.designs.length - 1;

    const prev = () => {
        if (hasPrev()) {
            const prevIndex = currentIndex - 1;
            setCurrentIndex(prevIndex);
            props.onDesignChange(prevIndex);
        }
    };

    const next = () => {
        if (hasNext()) {
            const nextIndex = currentIndex + 1;
            setCurrentIndex(nextIndex);
            props.onDesignChange(nextIndex);
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.keyCode === 27) {
            event.stopPropagation();
            props.onClose();
        }

        if (event.keyCode === 37) {
            event.stopPropagation();
            prev();
        }

        if (event.keyCode === 39) {
            event.stopPropagation();
            next();
        }
    };

    const { designer, onClose } = props;
    const design = designer.profile.designs[currentIndex];

    return (
        <Portal>
            <FullscreenOverlay
                isClosing={false}
                isActive={true}
                hasOpeningAndClosingAnimation={false}
                isScrollableSmallScreens={true}
                enableScroll={false}
            >
                <div ref={containerRef} onKeyDown={handleKeyDown} tabIndex={0}>
                    <FullscreenFrame.Frame>
                        <FullscreenFrame.Main>
                            <FullscreenMain.Main hasHeader={true}>
                                <FullscreenMain.Header>
                                    <FullscreenHeader.Header variations={['design-details']}>
                                        <div className="file-overlay__pagination">
                                            <div className="file-overlay__pagination__item">
                                                <IconButton
                                                    variations={['on-white']}
                                                    isDisabled={!hasPrev()}
                                                    onClick={prev}
                                                >
                                                    <Icon type={'left-2'} variations={['2x']} />
                                                </IconButton>
                                            </div>
                                            <div className="file-overlay__pagination__item">
                                                <strong>
                                                    {currentIndex + 1} /{' '}
                                                    {designer.profile.designs.length}
                                                </strong>
                                            </div>
                                            <div className="file-overlay__pagination__item">
                                                <IconButton
                                                    variations={['on-white']}
                                                    isDisabled={!hasNext()}
                                                    onClick={next}
                                                >
                                                    <Icon type={'right-2'} variations={['2x']} />
                                                </IconButton>
                                            </div>
                                        </div>
                                    </FullscreenHeader.Header>
                                </FullscreenMain.Header>
                                <FullscreenMain.Body variations={['design-details']}>
                                    <LoadableImage url={design.original} title={design.title} />
                                </FullscreenMain.Body>
                            </FullscreenMain.Main>
                        </FullscreenFrame.Main>
                        <FullscreenFrame.Sidebar>
                            <FullscreenSidebar.Main>
                                <DesignerDetails designer={designer} onAddDesigner={onClose} />
                            </FullscreenSidebar.Main>
                        </FullscreenFrame.Sidebar>
                    </FullscreenFrame.Frame>
                </div>
                <div className="file-overlay__close-button">
                    <IconButton onClick={onClose} variations={['on-white']}>
                        <Icon type={'x-3'} />
                    </IconButton>
                </div>
            </FullscreenOverlay>
        </Portal>
    );
};

export default FileOverlay;
